import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { fetch } from '@ngrx/router-store/data-persistence';
import { Store } from '@ngrx/store';
import { Observable, map, tap } from 'rxjs';

import { PageResponse, Pagination } from '@core/shared/domain';
import { NotificationService, ToastDuration, ToastUrgency, getPaginationParamsAfterDeletion } from '@core/shared/util';

import { FavoritesList, FavoritesListArticle } from '../../common';
import { FavoritesService } from '../services';

import { FavoritesActions, FavoritesMenuActions } from './favorites.actions';
import { selectFavoriteListArticlesPaginationMetadata } from './favorites.selectors';

@Injectable()
export class FavoritesEffects {
  fetchFavoritesLists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.fetchFavoritesLists),
      fetch({
        run: () => {
          return this.favoritesService
            .fetchFavoritesLists()
            .pipe(map((favoritesLists) => FavoritesActions.fetchFavoritesListsSuccess({ favoritesLists })));
        },
        onError: () => {
          this.notificationService.toastDanger('Das Laden der Listen ist fehlgeschlagen. Bitte versuche es nochmal.');
          return FavoritesActions.fetchFavoritesListsError();
        },
      }),
    );
  });

  fetchFavoriteListArticles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.fetchFavoriteListArticles),
      fetch({
        run: ({ listId, pagination }) => {
          return this.favoritesService.getArticlesForList(listId, pagination).pipe(
            map((favoriteListArticlesPageResponse: PageResponse<FavoritesListArticle>) =>
              FavoritesActions.fetchFavoriteListArticlesSuccess({
                favoriteListArticlesPageResponse,
              }),
            ),
          );
        },
        onError: () => FavoritesActions.fetchFavoriteListArticlesError(),
      }),
    );
  });

  createFavoritesList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.createFavoritesList),
      fetch({
        id: ({ createDto }) => createDto,
        run: ({ createDto }) => {
          return this.favoritesService.createList(createDto).pipe(
            map((createdList) =>
              FavoritesActions.createFavoritesListSuccess({
                createdList,
                globalArticleId: createDto.globalArticleId,
              }),
            ),
            tap(() => this.notificationService.toastSuccess('Die Liste wurde erfolgreich erstellt.')),
          );
        },
        onError: () => {
          this.notificationService.toastCustom(
            'Das Ertellen der Liste ist fehlgeschlagen. Bitte prüfe deine Eingabe und versuche es nochmal.',
            ToastDuration.Long,
            ToastUrgency.Danger,
          );
          return FavoritesActions.createFavoritesListError();
        },
      }),
    );
  });

  updateFavoritesList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.updateFavoritesList),
      fetch({
        id: ({ listId }) => listId,
        run: ({ listId, updateDto }) => {
          return this.favoritesService.updateList(listId, updateDto).pipe(
            map((updatedList) => FavoritesActions.updateFavoritesListSuccess({ updatedList })),
            tap(() => this.notificationService.toastSuccess('Die Änderungen wurden gespeichert.')),
          );
        },
        onError: () => {
          this.notificationService.toastCustom(
            'Das Speichern der aktualisierten Liste ist fehlgeschlagen. Bitte prüfe deine Eingabe und versuche es nochmal.',
            ToastDuration.Long,
            ToastUrgency.Danger,
          );
          return FavoritesActions.updateFavoritesListError();
        },
      }),
    );
  });

  addFavoriteListArticle$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesMenuActions.addFavoriteListArticle),
      fetch({
        id: ({ addToListDto }) => addToListDto.listId,
        run: ({ addToListDto }) => {
          return this.favoritesService.addArticleToList(addToListDto.listId, addToListDto.globalArticleId).pipe(
            map(() =>
              FavoritesMenuActions.addFavoriteListArticleSuccess({
                listId: addToListDto.listId,
              }),
            ),
            tap(() => this.notificationService.toastSuccess('Der Artikel wurde der Favoritenliste hinzugefügt.')),
          );
        },
        onError: () => FavoritesMenuActions.addFavoriteListArticleError(),
      }),
    );
  });

  deleteFavoritesList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.deleteFavoritesList),
      fetch({
        id: ({ listId }) => listId,
        run: ({ listId }) => {
          return this.favoritesService.deleteList(listId).pipe(
            map(() => FavoritesActions.deleteFavoritesListSuccess({ listId })),
            tap(() => this.notificationService.toastSuccess('Die Liste wurde erfolgreich gelöscht.')),
          );
        },
        onError: () => {
          this.notificationService.toastDanger('Das Löschen der Liste ist fehlgeschlagen. Bitte versuche es nochmal.');
          return FavoritesActions.deleteFavoritesListError();
        },
      }),
    );
  });

  deleteFavoriteListArticle$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.deleteFavoriteListArticle),
      fetch({
        id: ({ globalArticleId }) => globalArticleId,
        run: ({ listId, globalArticleId }) => {
          return this.removeArticleFromList(listId, globalArticleId).pipe(
            map((updatedList: FavoritesList) => FavoritesActions.deleteFavoriteListArticleSuccess({ updatedList })),
          );
        },
        onError: () => FavoritesActions.deleteFavoriteListArticleError(),
      }),
    );
  });

  deleteFavoriteListArticleSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesActions.deleteFavoriteListArticleSuccess),
      map(({ updatedList }) => updatedList.favoritesListId),
      concatLatestFrom(() => this.store$.select(selectFavoriteListArticlesPaginationMetadata)),
      map(([listId, paginationMetadata]) => ({
        listId,
        pagination: getPaginationParamsAfterDeletion(paginationMetadata) as Pagination,
      })),
      map(({ listId, pagination }) => FavoritesActions.fetchFavoriteListArticles({ listId, pagination })),
    );
  });

  deleteFavoriteListArticleFromMenu$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoritesMenuActions.deleteFavoriteListArticle),
      fetch({
        id: ({ globalArticleId }) => globalArticleId,
        run: ({ listId, globalArticleId }) => {
          return this.removeArticleFromList(listId, globalArticleId).pipe(
            map((updatedList: FavoritesList) => FavoritesMenuActions.deleteFavoriteListArticleSuccess({ updatedList })),
          );
        },
        onError: () => FavoritesMenuActions.deleteFavoriteListArticleError(),
      }),
    );
  });

  constructor(
    private readonly actions$: Actions,
    private readonly favoritesService: FavoritesService,
    private readonly notificationService: NotificationService,
    private readonly store$: Store,
  ) {}

  private removeArticleFromList(listId: string, globalArticleId: string): Observable<FavoritesList> {
    return this.favoritesService
      .removeArticleFromList(listId, globalArticleId)
      .pipe(tap(() => this.notificationService.toastSuccess('Der Artikel wurde aus der Favoritenliste entfernt.')));
  }
}
